import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export const useWebSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const getWebSocketUrl = useCallback(() => {
    // Use current window location to build WebSocket URL
    // This works with any domain (custom or default)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/api/ws/${userId}`;
  }, [userId]);

  const connect = useCallback(() => {
    if (!userId) return;

    try {
      const wsUrl = getWebSocketUrl();
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        
        // Iniciar ping para mantener conexión viva
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, WS_PING_INTERVAL);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Ignorar pong
          if (data.type === 'pong') return;
          
          setLastMessage(data);
          
          // Mostrar notificación toast según el tipo
          if (data.type === 'new_alert') {
            const alertData = data.data;
            const severityColors = {
              'CRITICA': '#ef4444',
              'ALTA': '#f97316',
              'MEDIA': '#eab308',
              'BAJA': '#22c55e'
            };
            
            toast.info(
              <div className="flex flex-col gap-1">
                <span className="font-semibold">Nueva Alerta</span>
                <span className="text-sm">{alertData.titulo}</span>
                <span 
                  className="text-xs font-medium px-2 py-0.5 rounded w-fit"
                  style={{ 
                    backgroundColor: severityColors[alertData.severidad] || '#64748b',
                    color: 'white'
                  }}
                >
                  {alertData.severidad}
                </span>
              </div>,
              {
                duration: 6000,
                position: 'top-right',
              }
            );
          } else if (data.type === 'alert_update') {
            toast.info(
              <div className="flex flex-col gap-1">
                <span className="font-semibold">Alerta Actualizada</span>
                <span className="text-sm">Nuevo estado: {data.data.new_status}</span>
              </div>,
              {
                duration: 4000,
                position: 'top-right',
              }
            );
          }
        } catch (error) {
          console.error('WebSocket: Error parseando mensaje', error);
        }
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        
        // Limpiar ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconectar automáticamente
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket: Error', error);
      };
    } catch (error) {
      console.error('WebSocket: Error creando conexión', error);
    }
  }, [userId, getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (userId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    reconnect: connect,
    disconnect
  };
};

export default useWebSocket;
